import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Badge,
  Switch,
  Spinner,
  Text,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  ToastBody,
} from '@fluentui/react-components';
import {
  PersonAdd24Regular,
  Edit20Regular,
  Key20Regular,
  PersonDelete20Regular,
  PersonAvailable20Regular,
} from '@fluentui/react-icons';
import Header from '../components/Header';
import DataTable, { ColumnConfig, RowMenuItem } from '../components/DataTable';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { UserFormDialog } from '../components/UserFormDialog';
import { ResetPasswordDialog } from '../components/ResetPasswordDialog';
import { queryKeys } from '../lib/queryClient';
import { formatBusinessDate } from '../lib/businessTime';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  StaffUser,
  UserRole,
  USER_ROLES,
  CreateUserInput,
  UpdateUserInput,
  fetchUsers,
  createUser,
  updateUser,
  resetUserPassword,
  deactivateUser,
  reactivateUser,
} from '../services/userService';

const useStyles = makeStyles({
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Floor to the viewport so flexGrow can't collapse the card to content
    // height when the nested flex height chain fails to resolve (gray gap
    // below the work surface on scroll). flexShrink:0 prevents re-squeeze.
    minHeight: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.margin('8px'),
    ...shorthands.overflow('hidden'),
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
  },
  tableArea: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    ...shorthands.padding('0', '8px', '8px'),
  },
  inactiveToggle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
});

const ROLE_LABEL: Record<UserRole, string> = USER_ROLES.reduce(
  (acc, r) => ({ ...acc, [r.value]: r.label }),
  {} as Record<UserRole, string>,
);

function roleBadgeColor(role: UserRole): 'brand' | 'informative' | 'subtle' {
  if (role === 'owner') return 'brand';
  if (role === 'admin') return 'informative';
  return 'subtle';
}

function formatDate(iso: string | null): string {
  // last_login_at is a real event timestamp shown as a date — render in the
  // business timezone.
  if (!iso) return '—';
  return formatBusinessDate(iso);
}

const Users: React.FC = () => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAdminAuth();
  const actorRole = (currentUser?.role as UserRole) || 'office';

  const toasterId = useId('users-toaster');
  const { dispatchToast } = useToastController(toasterId);
  const notify = (title: string, intent: 'success' | 'error', body?: string) =>
    dispatchToast(
      <Toast>
        <ToastTitle>{title}</ToastTitle>
        {body && <ToastBody>{body}</ToastBody>}
      </Toast>,
      { intent },
    );

  const [includeInactive, setIncludeInactive] = useState(false);

  // Dialog state.
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editTarget, setEditTarget] = useState<StaffUser | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [resetTarget, setResetTarget] = useState<StaffUser | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const [deactivateTarget, setDeactivateTarget] = useState<StaffUser | null>(null);

  const {
    data: users = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [...queryKeys.users.all, includeInactive] as const,
    queryFn: () => fetchUsers(includeInactive),
    staleTime: 5 * 60 * 1000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.users.all });

  const createMutation = useMutation({
    mutationFn: (input: CreateUserInput) => createUser(input),
    onSuccess: (u) => {
      invalidate();
      setFormOpen(false);
      notify('User created', 'success', `${u.name || u.email} can now sign in.`);
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      updateUser(id, input),
    onSuccess: (u) => {
      invalidate();
      setFormOpen(false);
      notify('User updated', 'success', u.name || u.email);
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const resetMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      resetUserPassword(id, password),
    onSuccess: () => {
      setResetTarget(null);
      notify('Password reset', 'success', 'Share the temporary password with the user.');
    },
    onError: (e: Error) => setResetError(e.message),
  });

  // Optimistically flip `active` across every cached users list so the row
  // responds on click instead of waiting on the refetch. Each list is keyed by
  // its `includeInactive` flag (the suffix of the query key): the active-only
  // list (`false`) drops the row, the include-inactive list (`true`) keeps it
  // with the new status. Snapshot/roll back on error, reconcile on settle.
  const patchActiveOptimistically = async (id: string, active: boolean) => {
    await queryClient.cancelQueries({ queryKey: queryKeys.users.all });
    const snapshots = queryClient.getQueriesData<StaffUser[]>({
      queryKey: queryKeys.users.all,
    });
    snapshots.forEach(([key, list]) => {
      if (!list) return;
      const includeInactive = key[key.length - 1] === true;
      const next = includeInactive
        ? list.map((u) =>
            u.id === id
              ? { ...u, active, deactivated_at: active ? null : new Date().toISOString() }
              : u,
          )
        : list.filter((u) => u.id !== id);
      queryClient.setQueryData(key, next);
    });
    return { snapshots };
  };

  const rollbackActive = (
    context: { snapshots: [readonly unknown[], StaffUser[] | undefined][] } | undefined,
  ) => {
    context?.snapshots.forEach(([key, list]) => queryClient.setQueryData(key, list));
  };

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deactivateUser(id),
    onMutate: (id: string) => patchActiveOptimistically(id, false),
    onError: (e: Error, _id, context) => {
      rollbackActive(context);
      notify('Could not deactivate', 'error', e.message);
    },
    onSuccess: () => notify('User deactivated', 'success'),
    onSettled: invalidate,
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => reactivateUser(id),
    onMutate: (id: string) => patchActiveOptimistically(id, true),
    onError: (e: Error, _id, context) => {
      rollbackActive(context);
      notify('Could not reactivate', 'error', e.message);
    },
    onSuccess: () => notify('User reactivated', 'success'),
    onSettled: invalidate,
  });

  const openCreate = () => {
    setFormMode('create');
    setEditTarget(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (u: StaffUser) => {
    setFormMode('edit');
    setEditTarget(u);
    setFormError(null);
    setFormOpen(true);
  };

  const columns: ColumnConfig<StaffUser>[] = useMemo(
    () => [
      {
        columnKey: 'name',
        label: 'Name',
        sortable: true,
        width: '220px',
        render: (u) => (
          <Text weight="semibold">{u.name || `${u.firstname || ''} ${u.lastname || ''}`.trim() || '—'}</Text>
        ),
      },
      {
        columnKey: 'email',
        label: 'Email',
        sortable: true,
        width: '260px',
        render: (u) => <span>{u.email}</span>,
      },
      {
        columnKey: 'role',
        label: 'Role',
        sortable: true,
        groupable: true,
        width: '120px',
        groupValueFormatter: (v) => ROLE_LABEL[v as UserRole] || v,
        render: (u) => (
          <Badge appearance="tint" color={roleBadgeColor(u.role)}>
            {ROLE_LABEL[u.role] || u.role}
          </Badge>
        ),
      },
      {
        columnKey: 'active',
        label: 'Status',
        sortable: true,
        width: '110px',
        render: (u) =>
          u.active ? (
            <Badge appearance="tint" color="success">Active</Badge>
          ) : (
            <Badge appearance="tint" color="danger">Deactivated</Badge>
          ),
      },
      {
        columnKey: 'job_title',
        label: 'Job title',
        sortable: true,
        width: '160px',
        render: (u) => <span className={u.job_title ? undefined : styles.muted}>{u.job_title || '—'}</span>,
      },
      {
        columnKey: 'last_login_at',
        label: 'Last login',
        sortable: true,
        width: '130px',
        render: (u) => <span className={styles.muted}>{formatDate(u.last_login_at)}</span>,
      },
    ],
    [styles.muted],
  );

  const rowMenuItems: RowMenuItem[] = useMemo(
    () => [
      {
        key: 'edit',
        label: 'Edit',
        icon: <Edit20Regular />,
        onClick: (u: StaffUser) => openEdit(u),
      },
      {
        key: 'reset',
        label: 'Reset password',
        icon: <Key20Regular />,
        onClick: (u: StaffUser) => {
          setResetTarget(u);
          setResetError(null);
        },
      },
      {
        key: 'deactivate',
        label: 'Deactivate',
        icon: <PersonDelete20Regular />,
        // Hide for already-inactive rows and for your own account (the backend
        // also blocks self-deactivation and removing the last owner).
        hidden: (u: StaffUser) => !u.active || u.email === currentUser?.email,
        onClick: (u: StaffUser) => setDeactivateTarget(u),
      },
      {
        key: 'reactivate',
        label: 'Reactivate',
        icon: <PersonAvailable20Regular />,
        hidden: (u: StaffUser) => u.active,
        onClick: (u: StaffUser) => reactivateMutation.mutate(u.id),
      },
    ],
    [currentUser?.email],
  );

  return (
    <>
      <div className={styles.workspaceCard}>
        <Header
          inline
          customTitle={<span style={{ fontSize: '20px', fontWeight: 600 }}>Users</span>}
          leftActions={
            <label className={styles.inactiveToggle}>
              <Switch
                checked={includeInactive}
                onChange={(_, d) => setIncludeInactive(d.checked)}
              />
              <Text size={200}>Show deactivated</Text>
            </label>
          }
          customActions={
            <Button appearance="primary" icon={<PersonAdd24Regular />} onClick={openCreate}>
              Add user
            </Button>
          }
        />

        <div className={styles.tableArea}>
          {isLoading ? (
            <Spinner label="Loading users…" style={{ marginTop: '40px' }} />
          ) : error ? (
            <Text style={{ color: tokens.colorPaletteRedForeground1, padding: '24px' }}>
              {(error as Error).message}
            </Text>
          ) : (
            <DataTable
              data={users}
              columns={columns}
              keyField="id"
              showCheckboxes={false}
              onRowClick={openEdit}
              rowMenuItems={rowMenuItems}
              defaultSortColumn="name"
              itemsPerPage={50}
            />
          )}
        </div>
      </div>

      <UserFormDialog
        isOpen={formOpen}
        mode={formMode}
        user={editTarget}
        actorRole={actorRole}
        saving={createMutation.isPending || updateMutation.isPending}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmitCreate={(values) => {
          setFormError(null);
          createMutation.mutate(values);
        }}
        onSubmitEdit={(values) => {
          if (!editTarget) return;
          setFormError(null);
          updateMutation.mutate({ id: editTarget.id, input: values });
        }}
      />

      <ResetPasswordDialog
        isOpen={!!resetTarget}
        user={resetTarget}
        saving={resetMutation.isPending}
        error={resetError}
        onClose={() => setResetTarget(null)}
        onSubmit={(password) => {
          if (!resetTarget) return;
          setResetError(null);
          resetMutation.mutate({ id: resetTarget.id, password });
        }}
      />

      <ConfirmationDialog
        isOpen={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => {
          if (deactivateTarget) deactivateMutation.mutate(deactivateTarget.id);
        }}
        title="Deactivate user"
        message={`Deactivate ${deactivateTarget?.name || deactivateTarget?.email || 'this user'}?`}
        subtext="They will no longer be able to sign in. Their history is kept and you can reactivate them later."
        confirmButtonText="Deactivate"
        variant="danger"
      />

      <Toaster toasterId={toasterId} />
    </>
  );
};

export default Users;
